import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useJobs } from '../hooks/useJobs';
import { useCategories } from '../hooks/useCategories';
import { inventoryService } from '../services/inventoryService';

const BulkRemoveInventoryPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { jobs } = useJobs();
  const { categories } = useCategories();

  // Common fields for all removals
  const [commonData, setCommonData] = useState({
    job_id: searchParams.get('job_id') || '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  // Available items and search
  const [availableItems, setAvailableItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(''); // Category filter
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Removal list (items added by user)
  const [removalList, setRemovalList] = useState([]); // [{ item, quantity }]
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  useEffect(() => {
    fetchAvailableItems();
  }, []);

  const fetchAvailableItems = async () => {
    setLoading(true);
    try {
      const result = await inventoryService.getSummary(0, 1000);
      const itemsWithStock = result.data.filter(item => item.total_quantity > 0);
      setAvailableItems(itemsWithStock);
    } catch (err) {
      console.error('Error fetching items:', err);
      setError('Failed to load inventory items');
    } finally {
      setLoading(false);
    }
  };

  // Filter items for search dropdown (exclude already added items)
  const filteredItems = availableItems.filter(item => {
    const alreadyAdded = removalList.some(r => r.item.item_id === item.item_id);
    
    // If no search term, return items based on category filter
    // If search term exists, filter by name/category
    const matchesSearch = !searchTerm || (
      item.item_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const matchesCategory = !categoryFilter || item.category_name === categoryFilter;
    return !alreadyAdded && matchesSearch && matchesCategory;
  });

  // If category is selected, show all items; otherwise limit to 10 for search
  const searchResults = categoryFilter ? filteredItems : filteredItems.slice(0, 10);

  const addItemToList = (item) => {
    setRemovalList(prev => [...prev, { item, quantity: 1 }]);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleSearchBlur = () => {
    // Delay closing to allow clicking dropdown items
    setTimeout(() => setShowDropdown(false), 200);
  };

  const removeFromList = (itemId) => {
    setRemovalList(prev => prev.filter(r => r.item.item_id !== itemId));
  };

  const updateQuantity = (itemId, value) => {
    const qty = parseInt(value);
    if (!isNaN(qty) && qty > 0) {
      setRemovalList(prev => prev.map(r => 
        r.item.item_id === itemId ? { ...r, quantity: qty } : r
      ));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (removalList.length === 0) {
      alert('Please add at least one item to remove');
      return;
    }

    if (!commonData.job_id) {
      alert('Please select a job');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const removals = removalList.map(({ item, quantity }) =>
        inventoryService.removeInventory({
          item_id: item.item_id,
          quantity,
          job_id: parseInt(commonData.job_id),
          date: commonData.date,
          notes: commonData.notes || undefined
        })
      );

      await Promise.all(removals);
      
      alert(`Successfully allocated ${removalList.length} item${removalList.length !== 1 ? 's' : ''} to job`);
      navigate('/inventory/summary');
    } catch (err) {
      console.error('Error removing inventory:', err);
      setError(err.response?.data?.detail || 'Failed to remove inventory');
    } finally {
      setLoading(false);
    }
  };

  const totalItems = removalList.length;
  const totalUnits = removalList.reduce((sum, r) => sum + r.quantity, 0);

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="bi bi-boxes me-2"></i>
          Allocate Inventory
        </h2>
        <button className="btn btn-secondary" onClick={() => navigate('/inventory/summary')}>
          <i className="bi bi-arrow-left me-2"></i>
          Back
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Common Information */}
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">Common Information</h5>
            <p className="text-muted small mb-3">
              These details will be applied to all items being sent to the job
            </p>
            
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Job <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={commonData.job_id}
                  onChange={(e) => setCommonData({ ...commonData, job_id: e.target.value })}
                  required
                >
                  <option value="">Select a job...</option>
                  {jobs.map(job => (
                    <option key={job.id} value={job.id}>
                      {job.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={commonData.date}
                  onChange={(e) => setCommonData({ ...commonData, date: e.target.value })}
                  required
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Notes (Optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={commonData.notes}
                  onChange={(e) => setCommonData({ ...commonData, notes: e.target.value })}
                  placeholder="Notes for all items..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Add Items */}
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">
              <i className="bi bi-search me-2"></i>
              Search & Add Items
            </h5>
            
            <div className="row mb-3">
              <div className="col-md-8">
                <div className="position-relative">
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search for items by name or category..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowDropdown(true);
                      }}
                      onFocus={() => setShowDropdown(true)}
                      onBlur={handleSearchBlur}
                      autoComplete="off"
                    />
                  </div>
                  
                  {/* Search Results Dropdown */}
                  {showDropdown && searchResults.length > 0 && (
                    <div 
                      className="list-group position-absolute w-100 shadow-lg mt-1" 
                      style={{ zIndex: 1000, maxHeight: '400px', overflowY: 'auto' }}
                    >
                      {searchResults.map(item => (
                        <button
                          key={item.item_id}
                          type="button"
                          className="list-group-item list-group-item-action"
                          onClick={() => addItemToList(item)}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong className="d-block">{item.item_name}</strong>
                              <small className="text-muted">
                                <span className="badge bg-info me-2">{item.category_name}</span>
                                {item.total_quantity} {item.unit_of_measure} available
                              </small>
                            </div>
                            <i className="bi bi-plus-circle-fill text-success fs-5"></i>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {showDropdown && searchResults.length === 0 && (
                    <div className="list-group position-absolute w-100 shadow mt-1" style={{ zIndex: 1000 }}>
                      <div className="list-group-item text-muted text-center">
                        No items found
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-4">
                <select
                  className="form-select"
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setShowDropdown(true);
                  }}
                >
                  <option value="">All Categories</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Allocation List */}
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="card-title mb-0">
                <i className="bi bi-list-check me-2"></i>
                Items to Allocate
              </h5>
              {totalItems > 0 && (
                <span className="badge bg-primary fs-6">
                  {totalItems} item{totalItems !== 1 ? 's' : ''} ({totalUnits} total units)
                </span>
              )}
            </div>

            {removalList.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-inbox display-1 text-muted d-block mb-3"></i>
                <p className="text-muted">
                  No items added yet. Use the search above to add items to your allocation list.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Item Name</th>
                      <th>Category</th>
                      <th className="text-end">Available</th>
                      <th>Unit</th>
                      <th style={{ width: '150px' }}>Quantity</th>
                      <th style={{ width: '100px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {removalList.map(({ item, quantity }) => (
                      <tr key={item.item_id}>
                        <td>
                          <strong>{item.item_name}</strong>
                        </td>
                        <td>
                          <span className="badge bg-info">{item.category_name}</span>
                        </td>
                        <td className="text-end">
                          <strong>{item.total_quantity}</strong>
                        </td>
                        <td>{item.unit_of_measure}</td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={quantity}
                            onChange={(e) => updateQuantity(item.item_id, e.target.value)}
                            min="1"
                            max={item.total_quantity}
                            step="1"
                            required
                          />
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeFromList(item.item_id)}
                            title="Remove from list"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="mt-4 d-flex justify-content-end gap-2 mb-5">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/inventory/summary')}
          >
            <i className="bi bi-x-circle me-2"></i>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || totalItems === 0}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Processing...
              </>
            ) : (
              <>
                <i className="bi bi-briefcase me-2"></i>
                Send {totalItems} Item{totalItems !== 1 ? 's' : ''} ({totalUnits} Unit{totalUnits !== 1 ? 's' : ''}) to Job
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BulkRemoveInventoryPage;
